import { html, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import type { ClubThousand } from '../../../app/package/base/cms/package/cms-domain';
import { UIModalServiceModal } from '../../../app/package/base/service/modals/modal-service';
import { TrackableEventAction, TrackingEventSource } from '../../../app/package/base/service/tracking/tracking-service';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { isEmpty } from '../../base/package/util/radash-helper';
import styles from './ui-club-1000-card-modal-info.css?inline';

const CName = 'ui-club-1000-card-info-modal';

/**
 * Events fired by the ui-modal component:
 * @fires modal-opened - This event is fired when the modal is opened. Will be caught by the app to control body scrolling.
 * @fires modal-closed - This event is fired when the modal is closed. Will be caught by the app to control body scrolling.
 */
@customElement(CName)
export class UiClub1000CardInfoModal extends UIModalServiceModal {
  static readonly styles = unsafeCSS(styles);

  @property({ type: String }) class = '';
  @property({ type: Boolean }) loggedIn = false;
  @property({ type: Object }) clubThousand: Omit<ClubThousand, 'order' | 'show_on_homepage' | 'translations'>;

  public close() {
    this._modalRef.value?.close();
  }

  render() {
    return html`
      <ui-modal ${ref(this._modalRef)}>
        <div slot="title" class="title">${this.clubThousand.title}</div>
        <div slot="main">
          <img src=${this.clubThousand.detail_image} class="detail-image" alt=${this.clubThousand.title} />
          ${this._renderDetailButtonWithWrapper()}
          <div class="subtitle">${this.clubThousand.subtitle}</div>
          <ui-rich-text class="card-description" .content=${this.clubThousand.card_description}></ui-rich-text>
          <ui-rich-text class="detail-description" .content=${this.clubThousand.detail_description}></ui-rich-text>
        </div>
      </ui-modal>
    `;
  }

  private _renderDetailButtonWithWrapper() {
    return this.clubThousand.game_id
      ? html`<div class="detail-button-wrapper">${this._renderPlayOrLoginButton()}</div>`
      : nothing;
  }

  private _renderPlayOrLoginButton() {
    return this.loggedIn
      ? html`
          <ui-btn
            .isBlock=${true}
            variant="secondary"
            class="club-1000-button"
            @click=${() => {
              this.close();
              this._trackGameClick();
              window.$app.router.navigateToGame(true, this.clubThousand.game_id!);
            }}
          >
            ${window.$app.$t.get(I18n.base.play)}
          </ui-btn>
        `
      : html`
          <ui-btn
            .isBlock=${true}
            variant="secondary"
            class="club-1000-button"
            @click=${() => {
              this.close();
              window.$app.product.gotoLogin(
                TrackingEventSource.CLUB1000X_LOBBY_PREVIEW,
                `/game/real/${this.clubThousand.game_id}`,
              );
            }}
          >
            ${window.$app.$t.get(I18n.base.login)}
          </ui-btn>
        `;
  }

  private _trackGameClick() {
    const gameId = this.clubThousand.game_id;

    if (!gameId || isEmpty(gameId)) {
      return;
    }

    window.$app.content
      .getGameInfo(gameId)
      .then(async (gameInfo) => {
        const game = await window.$app.content.getGame(gameId);
        if (game && gameId) {
          const trackingParams = {
            gameCategory: game.labels?.join(' - '),
            gameName: this.clubThousand.title,
            gameProvider: gameInfo.getStudio(),
            gameSource: 'club-1000x-modal',
            gamePosition: undefined,
            gameFilters: undefined,
            winType: undefined,
          };

          window.$app.trackingStore.next({ ...window.$app.trackingStore.value, ...trackingParams });

          window.$app.track.gamePlay({
            eventAction: TrackableEventAction.GAME_SELECTED,
            ...window.$app.trackingStore.value,
          });
        }
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info '${gameId}'`, err);
      });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiClub1000CardInfoModal;
  }
}
